import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwitchAccessShortcutAddIcon from '@mui/icons-material/SwitchAccessShortcutAdd';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { AsyncDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { FakeButtonLink, GridActionsCellItemLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { userFeeDeductionStatusToInfo } from '../../constants/tables/userFeeDeduction';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { createFeeBatch } from '../../redux/modules/feeBatch/feeBatch.actions';
import { feeBatchSelector } from '../../redux/modules/feeBatch/feeBatch.selectors';
import {
  fetchUserFeeDeductions,
  updateUserFeeDeductionStatus,
} from '../../redux/modules/userFeeDeduction/userFeeDeduction.actions';
import {
  userFeeDeductionByRequestIdSelector,
  userFeeDeductionSelector,
} from '../../redux/modules/userFeeDeduction/userFeeDeduction.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import { UserFeeDeduction, UserFeeDeductionStatus, UserFeeDeductionTypes } from '../../types/userFeeDeduction';
import * as permissions from '../../utils/permissions';
import { CreateBatchConfirmationDialog } from './CreateBatchConfirmationDialog';
import { round2Format } from '../../utils/number';

export function UserFeeDeductionTable() {
  const requestId = 'fee-batch-list';
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('apiError');
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef>(null);
  const [selectedRow, setSelectedRow] = useState<UserFeeDeduction>();
  const [actionMenu, setActionMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([
    // { ...GRID_CHECKBOX_SELECTION_COL_DEF, disableReorder: true, hideable: false },
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
    },
    {
      field: 'type.name',
      headerName: 'Type',
      type: 'singleSelect',
      valueOptions: Object.values(UserFeeDeductionTypes),
      width: 180,
      valueGetter: (params) => params.row?.type?.name,
      groupingValueGetter: (params) => params.row?.type?.name,
    },
    {
      field: 'type.name',
      headerName: 'Type',
      type: 'singleSelect',
      valueOptions: Object.values(UserFeeDeductionTypes),
      width: 180,
      valueGetter: (params) => params.row?.type?.name,
      groupingValueGetter: (params) => params.row?.type?.name,
    },
    {
      field: 'totalFee',
      headerName: 'Fee Amount',
      width: 100,
      renderCell: (params) => (params.value ? round2Format(params.value) : ''),
    },
    {
      field: 'userId',
      headerName: 'User',
      type: 'number',
      width: 120,
      groupingValueGetter: (params) => params.row?.userId,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <FakeButtonLink href={locations.user(params.value)} startIcon={<OpenInNewIcon />}>
            {params.value}
          </FakeButtonLink>
        );
      },
    },
    {
      field: 'userFee.id',
      headerName: 'Deduction id',
      type: 'number',
      width: 120,
      hideable: false,
      valueGetter: (params) => params.row?.userFee?.id,
      groupingValueGetter: (params) => params.row?.userFee?.id,
    },
    {
      field: 'userFee',
      headerName: 'User Fee',
      type: 'string',
      width: 120,
      valueGetter: (params) => {
        if (params.row?.id) {
          return `(${params.row?.userFee?.id})`;
        }
        return '';
      },
      groupingValueGetter: (params) => params.row?.userFee?.id,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <Tooltip title={params.value} followCursor>
            <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.userFee(params.row?.userId)}>
              {params.value}
            </FakeButtonLink>
          </Tooltip>
        );
      },
    },
    // {
    //   field: 'userPortfolio.cacheAllocationValue',
    //   headerName: 'Cache Allocation',
    //   type: 'number',
    //   width: 150,
    //   valueGetter: (params) => params.row?.userPortfolio?.cacheAllocationValue,
    // },
    // {
    //   field: 'userPortfolio.cacheAllocationPercentage',
    //   headerName: 'Cache Allocation %',
    //   type: 'number',
    //   width: 150,
    //   valueGetter: (params) => params.row?.userPortfolio?.cacheAllocationPercentage,
    // },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(UserFeeDeductionStatus),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = userFeeDeductionStatusToInfo[params.row?.status?.name as UserFeeDeductionStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const canDoActions = usePermissions({ items: [permissions.UserPortfolioRequest.statusUpdate] });
  const { updateStatusStatus } = useSelector(userFeeDeductionSelector);
  const { totalUserFeeDeductions, userFeeDeductions, fetchStatus } = useSelector((state: ReducerState) =>
    userFeeDeductionByRequestIdSelector(state, requestId),
  );
  const { createStatus: feeBatchCreateStatus } = useSelector(feeBatchSelector);
  const updateStatusLoading = updateStatusStatus === fetchStatuses.pending;
  const loading = fetchStatus === fetchStatuses.pending || feeBatchCreateStatus === fetchStatuses.pending;
  const availableStatuses = [UserFeeDeductionStatus.CANCELED, UserFeeDeductionStatus.REJECTED].filter(
    (s) => s !== selectedRow?.status?.name,
  ) as UserFeeDeductionStatus[];
  const firstSelectedItem = userFeeDeductions.find((item) => item.id === selectedIds?.[0]);
  // TODO: Find a way to store this
  // const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', search.get('filter') || '');
  // const [initialPage, setInitialPage] = useSearchParamsState('page', search.get('page') || '0');
  // const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', search.get('pageSize') || '10');

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, data: UserFeeDeduction) => {
    setSelectedRow(data);
    setActionMenu(
      actionMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null,
    );
  };

  const isRowSelectable = (params: GridRowParams<UserFeeDeduction>): boolean =>
    params.row?.status?.name === UserFeeDeductionStatus.PENDING &&
    (!firstSelectedItem || params.row?.type?.name === firstSelectedItem?.type?.name);

  const handleCloseMenu = () => {
    setActionMenu(null);
  };

  const updateStatus = async (id: number, status: UserFeeDeductionStatus) => {
    handleCloseMenu();

    try {
      await dispatch(updateUserFeeDeductionStatus({ id, status })).unwrap();

      tableRef?.current?.reload();
      enqueueSnackbar('User Fee Deduction status updated!', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(`User Fee Deduction update status error: ${t(error?.message)} ${JSON.stringify(error?.data)}`, {
        variant: 'error',
      });
    }
  };

  const onCreateBatch = async () => {
    if (!selectedIds.length) {
      return;
    }

    const isConfirm = await asyncDialogRef?.current?.show();

    if (!isConfirm) {
      return;
    }

    try {
      const {
        data: { batchId, executionId },
      } = await dispatch(createFeeBatch({ userFeeDeductionIds: selectedIds })).unwrap();

      tableRef?.current?.reload();
      enqueueSnackbar('Fee Batch created!', {
        variant: 'success',
        action: (
          <Grid container flexDirection="row">
            <Grid item>
              <Divider orientation="vertical" color="#fff" />
            </Grid>
            <Grid item>
              <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.feeBatch(batchId)} color="inherit">
                Batch
              </FakeButtonLink>
            </Grid>
            <Grid item>
              <FakeButtonLink
                startIcon={<OpenInNewIcon />}
                href={locations.batchExecution(executionId)}
                color="inherit"
              >
                Execution
              </FakeButtonLink>
            </Grid>
          </Grid>
        ),
      });
    } catch (error: any) {
      enqueueSnackbar(`Fee Batch creation error: ${t(error?.message)} ${JSON.stringify(error?.data)}`, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    setSelectedIds([]);
  }, [userFeeDeductions]);

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canDoActions && params.row.id) {
          actions.push(
            <GridActionsCellItem
              key={`${params.id}-employee-list-actions-${params.row.id}`}
              icon={<MoreVertIcon />}
              label="Actions"
              onClick={(e) => handleMenuOpen(e, params.row)}
            />,
          );
        }

        if (`${params.id}`.startsWith('auto-generated-row-userFee.id/')) {
          const userFeeDeductionId = `${params.id}`.substring(`${params.id}`.lastIndexOf('/') + 1);

          actions.push(
            <GridActionsCellItemLink
              key={`${params.id}-details-action`}
              icon={<OpenInNewIcon />}
              label="Details"
              href={locations.userFeeDeduction(userFeeDeductionId)}
            />,
          );
        }

        return actions;
      },
    };

    if (canDoActions) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canDoActions]);

  useEffect(() => {
    let ids = selectedIds;
    const firstItem = userFeeDeductions.find((item) => item.id === ids?.[0]);

    if (firstItem) {
      ids = ids.filter((id) => {
        const fItem = userFeeDeductions.find((item) => item.id === id);

        return fItem?.type?.name === firstItem?.type?.name;
      });
    }

    if (ids.length !== selectedIds.length) {
      setSelectedIds(ids);
    }
  }, [selectedIds]);

  return (
    <>
      <Table
        ref={tableRef}
        fetchItems={fetchUserFeeDeductions}
        rows={userFeeDeductions}
        columns={columns}
        loading={loading}
        rowCount={totalUserFeeDeductions}
        checkboxSelection
        isRowSelectable={isRowSelectable}
        rowSelectionModel={selectedIds}
        onSelectionModelChange={(sm) => setSelectedIds(sm as number[])}
        // onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        // onPageChange={(page) => setInitialPage(`${page}`)}
        // onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
        disableRowGrouping={false}
        rowGroupingColumnMode="single"
        initialRowGroupingModel={['userFee.id']}
        defaultGroupingExpansionDepth={1}
        initialColumnVisibilityModel={{ 'type.name': false, 'userFee.id': false }}
        // requiredFilters={[{ field: 'type.name', value: type, operator: 'is', id: 1 }]}
        // initialFilterModel={parseJSON(initialFilterModel)}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        // initialPage={+initialPage}
        // initialPageSize={+initialPageSize}
        sortingMode="client"
        fetchItemsCustomParams={{ requestId }}
        customToolbarItems={[
          <Button
            id="NewBatchButton"
            key="user-fee-custom-toolbar-1"
            disabled={!selectedIds.length}
            startIcon={<SwitchAccessShortcutAddIcon />}
            onClick={onCreateBatch}
          >
            New Batch {!!selectedIds.length && `(${selectedIds.length} requests)`}
          </Button>,
        ]}
        groupingColDef={(params) => {
          const override: GridGroupingColDefOverride = {};
          if (params.fields.includes('userFee.id')) {
            return {
              headerName: 'Request',
              filterable: false,
              sortable: false,
              hideable: false,
            };
          }

          return override;
        }}
      />
      <Menu
        open={actionMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          actionMenu !== null
            ? {
                top: actionMenu.mouseY,
                left: actionMenu.mouseX,
              }
            : undefined
        }
      >
        {availableStatuses.map((status) => {
          const info = userFeeDeductionStatusToInfo[status] || {};

          return (
            <MenuItem
              key={`user-fee-deduction-list-menu-action-${status}`}
              onClick={() => selectedRow && updateStatus(selectedRow.id, status)}
              disabled={updateStatusLoading}
            >
              {info.menuAction}
            </MenuItem>
          );
        })}
      </Menu>
      <AsyncDialog ref={asyncDialogRef} dialog={CreateBatchConfirmationDialog} />
    </>
  );
}
